/**
 * Climate & Severe Weather presentation helpers.
 *
 * Pure functions that turn `climateSevereWeather` block + optional SED
 * climate evidence into a lead narrative plus supporting hazard bullets.
 * Used by the report viewer, DOCX export, and deterministic fallbacks.
 */
import type { SiteEvidenceDocument } from '../types/siteEvidence';

export interface ClimateSevereWeatherBlock {
  risks?: string[];
  implications?: string;
}

export interface ClimateSiteContext {
  siteName?: string;
  address?: string;
}

export interface ClimateSevereWeatherPresentation {
  /** Lead narrative prose (1–2 paragraphs). Empty when no content at all. */
  summary: string;
  /** Supporting hazard bullets after de-duplication against the summary. */
  detailRisks: string[];
  /** Whether to render the "Key Risks" subheading above detailRisks. */
  showDetailHeading: boolean;
}

const THIN_NARRATIVE_PATTERNS: RegExp[] = [
  /^Climate observations:/i,
  /^Climate analysis pending/i,
  /^Climate values indicate (demanding|manageable)/i,
  /^Average wet bulb/i,
];

/** Detect fallback / data-dump strings that should not stand alone as prose. */
export function isThinClimateNarrative(text: string | undefined | null): boolean {
  const trimmed = (text ?? '').trim();
  if (trimmed.length === 0) return true;
  if (trimmed.length < 90 && THIN_NARRATIVE_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return true;
  }
  if (/^Climate observations:/i.test(trimmed) && !/[.!?]\s+[A-Z]/.test(trimmed)) {
    return true;
  }
  return false;
}

function isDataOnlyRisk(risk: string): boolean {
  return /^(Average wet bulb|Peak temperature|Humidity)\s/i.test(risk.trim());
}

function filterRedundantRisks(risks: string[], summary: string): string[] {
  const lowerSummary = summary.toLowerCase();
  return risks.filter((risk) => {
    if (!isDataOnlyRisk(risk)) return true;
    if (/wet.?bulb/i.test(risk) && /wet.?bulb/i.test(lowerSummary)) return false;
    if (/peak temperature/i.test(risk) && /peak/i.test(lowerSummary)) return false;
    if (/humidity/i.test(risk) && /humidity/i.test(lowerSummary)) return false;
    return true;
  });
}

function locationLabel(context?: ClimateSiteContext): string {
  const address = context?.address?.trim();
  if (address) return address;
  const name = context?.siteName?.trim();
  if (name) return name;
  return 'This site';
}

/**
 * Compose 1–2 sentences of narrative prose from SED climate evidence.
 */
export function composeClimateSummaryFromSed(
  sed: SiteEvidenceDocument | null | undefined,
  context?: ClimateSiteContext,
): string {
  const cd = sed?.climate?.data;
  if (!cd) return '';

  const loc = locationLabel(context);
  const sentences: string[] = [];

  const designWb =
    typeof cd.peakWetBulb === 'number' && Number.isFinite(cd.peakWetBulb)
      ? cd.peakWetBulb
      : typeof cd.avgWetBulb === 'number' && Number.isFinite(cd.avgWetBulb)
        ? cd.avgWetBulb
        : null;

  if (designWb !== null) {
    const coolingLoad =
      designWb >= 26
        ? 'demanding evaporative cooling performance and may require hybrid or dry-assist capacity at peak'
        : designWb >= 22
          ? 'moderate wet-cooling loads typical of warm-humid climates'
          : 'generally favorable conditions for wet cooling and cooling-tower efficiency';
    sentences.push(
      `${loc} has a wet-bulb screening value of approximately ${designWb.toFixed(1)}°C, indicating ${coolingLoad}. Confirm the applicable equipment design percentile and operating duty separately; a monthly screening statistic does not establish either.`,
    );
  }

  if (
    typeof cd.avgTemp === 'number' &&
    Number.isFinite(cd.avgTemp) &&
    typeof cd.peakTemp === 'number' &&
    Number.isFinite(cd.peakTemp)
  ) {
    sentences.push(
      `The climate regime averages ${cd.avgTemp.toFixed(1)}°C annually with peak summer highs around ${cd.peakTemp.toFixed(1)}°C.`,
    );
  }

  if (typeof cd.annualPrecipitation === 'number' && Number.isFinite(cd.annualPrecipitation)) {
    if (cd.annualPrecipitation >= 1200) {
      sentences.push(
        `Annual precipitation of roughly ${Math.round(cd.annualPrecipitation)} mm elevates stormwater management and pluvial-flood diligence during construction.`,
      );
    } else if (sentences.length < 2) {
      sentences.push(
        `Annual precipitation of roughly ${Math.round(cd.annualPrecipitation)} mm sits within typical continental ranges.`,
      );
    }
  }

  if (typeof cd.humidity === 'number' && Number.isFinite(cd.humidity) && sentences.length < 2) {
    sentences.push(`Average relative humidity of ${Math.round(cd.humidity)}% affects adiabatic and evaporative cooling performance.`);
  }

  return sentences.slice(0, 2).join(' ');
}

function synthesizeLeadFromRisks(risks: string[], context?: ClimateSiteContext): string {
  if (risks.length === 0) return '';
  const loc = locationLabel(context);
  return `${loc} faces climate and severe-weather exposure that affects cooling design, construction sequencing, and BTM plant hardening. The principal hazards for feasibility assessment are outlined below.`;
}

/**
 * Resolve viewer/export presentation: narrative first, bullets as detail.
 */
export function resolveClimateSevereWeatherPresentation(
  block: ClimateSevereWeatherBlock | undefined | null,
  sed?: SiteEvidenceDocument | null,
  context?: ClimateSiteContext,
): ClimateSevereWeatherPresentation {
  const risks = (block?.risks ?? []).filter(
    (risk): risk is string => typeof risk === 'string' && risk.trim().length > 0,
  );
  const implications = block?.implications?.trim() ?? '';

  let summary = '';
  if (implications && !isThinClimateNarrative(implications)) {
    summary = implications;
  } else {
    const fromSed = composeClimateSummaryFromSed(sed, context);
    if (fromSed) {
      summary = fromSed;
    } else if (implications) {
      summary = implications;
    } else if (risks.length > 0) {
      summary = synthesizeLeadFromRisks(risks, context);
    }
  }

  const detailRisks = filterRedundantRisks(risks, summary);
  const showDetailHeading = detailRisks.length > 0;

  return { summary, detailRisks, showDetailHeading };
}
